import toTree from "@/utils/toTree";
import keyBy from "lodash/keyBy";

/**
 * 处理树搜索类
 * 使用方法，可参考 src\views\orgPersonnel\departmentManagement\treeTable\index.vue
 * 
 * 1. 直接在data中定义treeHandle
 * 2. 通过接口获取到树数据后，调用 treeHandle.updata(treeData) 更新
 * 3. 搜索的时候，调用 treeHandle.search(keyword), treeHandle里的 searchTree 和 expandkey 会自动更新
 * 2. 为了性能考虑，应用新的组件渲染搜索处理的树，而不是公用同一个树组件
 * 
 * 注意：
 * 一旦使用了这个类，会对源数据进行修改，会对每个树节点添加 parentNode 和 parent_id 属性
 * searchTree 中的节点均为源数据的浅拷贝，并且为搜索命中的节点加上 hitSearch 属性。
 * 
 */
export default class SearchTree {
  constructor(options) {
    Object.assign(this.options, options);
  }

  options = {
    childrenProp: "children",
    idProp: "id",
    checkFn: () => false,
  };

  treeData = []; // 树的源数据
  allTreeNode = []; // 树的所有节点数据
  allTreeNodeMap = {}; // 树的所有节点索引

  searchTree = []; // 搜索后生成的树
  expandkey = []; // 搜索后生成的树要展开的id集合

  /**
   * 更新树数据，包括 treeData，allTreeNode，allTreeNodeMap
   * @param {*} treeData 不传就只更新allTreeNode，allTreeNodeMap
   */
  update(treeData) {
    if (treeData) {
      this.treeData = treeData;
    }

    this.allTreeNode = getAllTreeNode(treeData || this.treeData, {
      childrenProp: this.options.childrenProp,
      idProp: this.options.idProp
    });

    this.allTreeNodeMap = keyBy(this.allTreeNode, this.options.idProp);
  }

  /**
   * 搜索
   * @param {*} keywords 关键字
   */
  search(keywords) {
    let { checkFn, childrenProp, idProp } = this.options;
    let searchResultSet = new Set(); // 搜索结果，包含命中目标的祖先节点
    let expandkeySet = new Set(); // 要展开的树节点key

    // console.log('search', keywords)

    // 通过遍历找出所有的命中节点
    for (let node of this.allTreeNode) {
      if (this.options.checkFn(node, keywords)) {
        findSelfAndParentNode(node, searchResultSet);
      }
    }

    // console.log('searchResultSet', searchResultSet)

    // 生成新的树节点，以免污染源数据
    let newNodes = [...searchResultSet].map((item) => {
      // 浅拷贝
      let tmp = {
        ...item,
        [childrenProp]: null,
        _children: item[childrenProp], // 备份原有的子节点列表
        hitSearch: false, // 是否命中搜索
      };

      if (checkFn(item, keywords)) {
        tmp.hitSearch = true;
      }

      // 收集要展开的节点，一般为命中目标的父节点
      if (item.parent_id) {
        expandkeySet.add(item.parent_id);
      } else {
        expandkeySet.add(item[this.options.idProp]);
      }
      return tmp;
    });

    // 生成树
    let [tree, treeHash] = toTree(newNodes, { idProp, childrenProp }, true);

    Object.values(treeHash).forEach((item) => {
      // 如果没有 children，代表是叶子节点，根据业务需求，命中的目标是可以展开的
      setChildren(item, childrenProp);
    });

    this.searchTree = tree;
    this.expandkey = [...expandkeySet];
  }
}

/**
 * 遍历出所有的树节点, 并往节点里补充 parentNode，parent_id
 */
function getAllTreeNode(treeData, options, result = [], parentNode = null) {
  if (treeData && treeData.length) {
    treeData.forEach((node) => {
      node.parentNode = parentNode;
      node.parent_id = parentNode ? parentNode[options.idProp] : "";

      if (node[options.childrenProp]) {
        getAllTreeNode(node[options.childrenProp], options, result, node);
      }

      result.push(node);
    });

    return result;
  } else {
    return [];
  }
}

/**
 * 找出节点和其所有父节点
 */
function findSelfAndParentNode(target, result) {
  result.add(target);

  if (target.parentNode) {
    findSelfAndParentNode(target.parentNode, result);
  }
}

function setChildren(node, childrenProp) {
  if (!node[childrenProp]) {
    node[childrenProp] = node._children;
  }
}
